import { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { toast } from "sonner";

export default function Admin() {
  const [gate, setGate] = useState({ loading: true, is_admin: false });
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [learning, setLearning] = useState(null);
  const [learningLoading, setLearningLoading] = useState(false);
  const [learningDays, setLearningDays] = useState(60);
  const [sectorFilter, setSectorFilter] = useState("");
  const [profile, setProfile] = useState(null);
  const [profileUserId, setProfileUserId] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/admin/whoami").then(({ data }) => {
      setGate({ loading: false, ...data });
      if (data.is_admin) refresh();
    }).catch(() => setGate({ loading: false, is_admin: false }));
  }, []);

  const refresh = async () => {
    try {
      const [o, u, t] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/users?limit=50"),
        api.get("/admin/transactions?limit=50"),
      ]);
      setOv(o.data);
      setUsers(u.data.items || []);
      setTxs(t.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Admin data load failed");
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [{ data: main }, { data: ts }] = await Promise.all([
        api.get("/admin/analytics/trade-ideas?days=30"),
        api.get("/admin/analytics/timeseries?days=14"),
      ]);
      setAnalytics({ ...main, _timeseries: ts });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analytics load failed");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "analytics" && gate.is_admin && !analytics) loadAnalytics();
  }, [tab, gate.is_admin, analytics]);

  const loadLearning = async (days = learningDays, sector = sectorFilter) => {
    setLearningLoading(true);
    try {
      const q = `?days=${days}`;
      const sectorQP = sector ? `&sector=${encodeURIComponent(sector)}` : "";
      const [h, strat, sym, sec, prompt, calib, ub, drift, readi, sectors, leaderboard, regime] = await Promise.all([
        api.get(`/admin/learning/health`),
        api.get(`/admin/learning/strategy-accuracy${q}${sectorQP}`),
        api.get(`/admin/learning/symbol-accuracy${q}${sectorQP}`),
        api.get(`/admin/learning/sector-accuracy${q}`),
        api.get(`/admin/learning/prompt-performance${q}`),
        api.get(`/admin/learning/confidence-calibration${q}`),
        api.get(`/admin/learning/user-behavior${q}`),
        api.get(`/admin/learning/model-drift?days=${Math.max(days, 30)}&bucket_days=7`),
        api.get(`/admin/learning/signal-filter-readiness?days=${Math.max(days, 30)}`),
        api.get(`/admin/learning/sectors`),
        api.get(`/admin/learning/profiles/leaderboard?days=${Math.max(days, 30)}&limit=15`),
        api.get(`/admin/learning/regime-intelligence?days=${Math.max(days, 30)}`),
      ]);
      setLearning({
        health: h.data, strategy: strat.data, symbol: sym.data, sector: sec.data,
        prompt: prompt.data, calibration: calib.data, userBehavior: ub.data,
        drift: drift.data, readiness: readi.data,
        sectorsList: sectors.data?.sectors || [],
        leaderboard: leaderboard.data,
        regime: regime.data,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Learning framework load failed");
    } finally {
      setLearningLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "learning" && gate.is_admin && !learning) loadLearning();
  }, [tab, gate.is_admin, learning]);

  const triggerResolveNow = async () => {
    try {
      const r = await api.post(`/admin/learning/resolve-now?backfill_days=30&limit=100`);
      toast.success(
        `Resolver: ${r.data.counts.resolved_win} wins, ${r.data.counts.resolved_loss} losses, ${r.data.counts.resolved_neutral} neutrals`
      );
      setLearning(null);   // force reload
      loadLearning();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolver tick failed");
    }
  };

  const runPromptBackfill = async () => {
    try {
      const dry = await api.post(`/admin/learning/backfill-prompt-version`, { dry_run: true });
      const count = dry.data.ideas_needing_backfill;
      if (count === 0) {
        toast.info("No legacy ideas remaining — every idea already has a prompt version");
        return;
      }
      if (!window.confirm(`Backfill ${count} legacy trade_ideas with 'trade.v1_legacy'? (safe & idempotent)`)) return;
      const r = await api.post(`/admin/learning/backfill-prompt-version`, { dry_run: false });
      toast.success(`Backfilled ${r.data.ideas_updated} ideas + ${r.data.outcomes_updated} outcomes`);
      setLearning(null);
      loadLearning();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    }
  };

  const loadProfile = async (userId) => {
    if (!userId) return;
    setProfileLoading(true);
    try {
      const r = await api.get(`/admin/learning/profile/${encodeURIComponent(userId)}?days=180`);
      setProfile(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Profile load failed");
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  if (gate.loading) {
    return <div className="p-8 text-center text-xs font-mono-data text-[color:var(--text-muted)]">Verifying admin status…</div>;
  }

  if (!gate.is_admin) {
    return (
      <div className="p-8 text-center max-w-lg mx-auto" data-testid="admin-forbidden">
        <div className="font-serif-elite text-3xl text-white mb-3">Admin Only</div>
        <div className="text-sm text-[color:var(--text-secondary)] mb-4">
          This dashboard is restricted. Ask a platform administrator to add your email to <span className="font-mono-data text-gold">ADMIN_EMAILS</span> in the backend environment.
        </div>
        <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]">Your account: {gate.email || gate.user_id}</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="admin-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-6">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">
          ◆ ADMIN COST &amp; REVENUE DASHBOARD
        </div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Command Ops</h1>
      </div>

      {ov && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border-default)] mb-4" data-testid="kpi-strip">
            <KPI label="REVENUE · 30D" value={`$${fmt(ov.revenue_usd["30d"])}`} tone="bull" />
            <KPI label="LLM COST · 30D" value={`$${fmt(ov.llm_cost_usd["30d"], 4)}`} tone="bear" />
            <KPI label="MARGIN · 30D" value={`$${fmt(ov.revenue_usd["30d"] - ov.llm_cost_usd["30d"])}`} tone={ov.revenue_usd["30d"] - ov.llm_cost_usd["30d"] >= 0 ? "bull" : "bear"} />
            <KPI label="LIFETIME REV" value={`$${fmt(ov.revenue_usd.lifetime)}`} tone="gold" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border border-[color:var(--border-default)] mb-4" data-testid="user-metrics">
            <KPI label="USERS" value={fmt(ov.users.total, 0)} />
            <KPI label="NEW · 7D" value={fmt(ov.users.new_7d, 0)} />
            <KPI label="DAU" value={fmt(ov.users.dau, 0)} tone="bull" />
            <KPI label="WAU" value={fmt(ov.users.wau, 0)} />
            <KPI label="MAU" value={fmt(ov.users.mau, 0)} />
            <KPI label="PAYING %" value={`${ov.users.paying_rate_pct}%`} tone="gold" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border-default)] mb-6" data-testid="ops-metrics">
            <KPI label="CACHE HIT · 1D" value={`${ov.cache.hit_rate_pct}%`} tone="bull" />
            <KPI label="CALLS · 1D" value={fmt(ov.cache.total_1d, 0)} />
            <KPI label="LLM COST · 1D" value={`$${fmt(ov.llm_cost_usd["1d"], 4)}`} />
            <KPI label="CREDITS BURNED · 30D" value={fmt(ov.credits_used_30d, 0)} />
          </div>
        </>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border border-[color:var(--border-default)] mb-4 w-fit" data-testid="admin-tabs">
        {["overview", "analytics", "learning", "users", "transactions", "setup"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-[11px] font-mono-data tracking-widest border-r border-[color:var(--border-default)] last:border-r-0 transition-colors ${
              tab === t ? "bg-white text-black" : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"
            }`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "overview" && ov && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="overview-panels">
          <Panel title="MODEL SPLIT · 7D">
            <div className="space-y-1">
              {(ov.model_split_7d || []).map((m) => (
                <Row key={m._id || "unknown"} left={m._id?.replace("claude-", "") || "unknown"} right={`${m.count} calls · $${fmt(m.usd, 4)}`} />
              ))}
            </div>
          </Panel>
          <Panel title="TOP FEATURES · 7D (BY CREDITS)">
            <div className="space-y-1">
              {(ov.top_features_7d || []).map((f) => (
                <Row key={f._id} left={f._id?.replace(/_/g, " ")} right={`${f.n}× · ${f.credits} cr`} />
              ))}
            </div>
          </Panel>
          <Panel title="TOP USERS · 7D (BY CREDITS)">
            <div className="space-y-1">
              {(ov.top_users_7d || []).map((u) => (
                <Row key={u._id} left={u._id} right={`${u.n}× · ${u.credits} cr`} />
              ))}
            </div>
          </Panel>
        </div>
      )}

      {tab === "analytics" && (
        <AnalyticsPanel data={analytics} loading={analyticsLoading} onRefresh={loadAnalytics} />
      )}

      {tab === "learning" && (
        <LearningPanel
          data={learning}
          loading={learningLoading}
          days={learningDays}
          sectorFilter={sectorFilter}
          onChangeSector={(s) => { setSectorFilter(s); setLearning(null); loadLearning(learningDays, s); }}
          onChangeDays={(d) => { setLearningDays(d); setLearning(null); loadLearning(d, sectorFilter); }}
          onRefresh={() => { setLearning(null); loadLearning(); }}
          onResolveNow={triggerResolveNow}
          onBackfillPrompt={runPromptBackfill}
          profile={profile}
          profileLoading={profileLoading}
          profileUserId={profileUserId}
          onChangeProfileUserId={setProfileUserId}
          onLoadProfile={loadProfile}
        />
      )}

      {tab === "users" && (
        <div className="border border-[color:var(--border-default)]" data-testid="users-table">
          <div className="grid grid-cols-[1.2fr,1fr,80px,80px,80px,120px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
            <span>EMAIL</span><span>PHONE</span><span>PURCH</span><span>USED</span><span>FREE-USED</span><span>CREATED</span>
          </div>
          {users.map((u) => (
            <div key={u.user_id} className="grid grid-cols-[1.2fr,1fr,80px,80px,80px,120px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs">
              <span className="text-white truncate">{u.email || "—"}</span>
              <span className="font-mono-data text-[color:var(--text-secondary)]">{u.phone || "—"}</span>
              <span className="font-mono-data text-gold">{u.credits?.purchased || 0}</span>
              <span className="font-mono-data text-bear">{u.credits?.lifetime_used || 0}</span>
              <span className="font-mono-data text-[color:var(--text-secondary)]">{u.credits?.free_used_this_month || 0}</span>
              <span className="font-mono-data text-[color:var(--text-muted)]">{(u.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "transactions" && (
        <div className="border border-[color:var(--border-default)]" data-testid="transactions-table">
          <div className="grid grid-cols-[1fr,80px,80px,80px,1fr,120px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
            <span>SESSION</span><span>ITEM</span><span>$</span><span>CRED</span><span>STATUS</span><span>DATE</span>
          </div>
          {txs.map((t) => (
            <div key={t.session_id} className="grid grid-cols-[1fr,80px,80px,80px,1fr,120px] gap-2 px-3 py-2 border-b border-[color:var(--border-default)] text-xs">
              <span className="font-mono-data text-white truncate">{t.session_id?.slice(0, 24)}…</span>
              <span className="font-mono-data text-[color:var(--text-secondary)]">{t.item_id}</span>
              <span className="font-mono-data text-gold">${t.amount}</span>
              <span className="font-mono-data text-white">{t.credits}</span>
              <span className={`font-mono-data ${t.credits_granted ? "text-bull" : "text-[color:var(--text-secondary)]"}`}>{t.payment_status}{t.credits_granted ? " · granted" : ""}</span>
              <span className="font-mono-data text-[color:var(--text-muted)]">{(t.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "setup" && <SetupPanel />}
    </div>
  );
}

function SetupPanel() {
  const [providers, setProviders] = useState({});
  const [brokerStatus, setBrokerStatus] = useState(null);
  const [ragBackend, setRagBackend] = useState(null);
  const [version, setVersion] = useState(null);
  useEffect(() => {
    api.get("/auth/oauth/providers").then(({ data }) => setProviders(data));
    api.get("/brokers/status").then(({ data }) => setBrokerStatus(data)).catch(() => {});
    api.get("/rag/backend-info").then(({ data }) => setRagBackend(data)).catch(() => {});
    api.get("/version").then(({ data }) => setVersion(data)).catch(() => {});
  }, []);
  const clientCommit = process.env.REACT_APP_BUILD_COMMIT || "unknown";
  const mismatch = version?.commit && version.commit !== clientCommit;

  const envGuide = [
    { section: "Deployment Version", vars: ["BUILD_COMMIT", "BUILD_TIME", "APP_VERSION", "REACT_APP_BUILD_COMMIT", "REACT_APP_BUILD_TIME", "REACT_APP_VERSION"], url: "/api/version", steps: ["Rebuild frontend on deploy", "Confirm CLI commit matches SRV commit in the version footer (bottom-right)", "If they diverge, redeploy is required"] },
    { section: "Apple Sign-In", vars: ["APPLE_TEAM_ID", "APPLE_KEY_ID", "APPLE_PRIVATE_KEY_PEM", "APPLE_SERVICES_ID"], url: "https://developer.apple.com/", steps: ["Create Services ID", "Enable Sign in with Apple", "Generate a p8 key (paste PEM into APPLE_PRIVATE_KEY_PEM)"] },
    { section: "Microsoft (Azure AD)", vars: ["MICROSOFT_CLIENT_ID", "MICROSOFT_CLIENT_SECRET", "MICROSOFT_TENANT_ID"], url: "https://portal.azure.com/", steps: ["Register an application", "Add redirect URI /api/auth/oauth/microsoft/callback", "Copy client_id + client_secret"] },
    { section: "GitHub", vars: ["GITHUB_CLIENT_ID", "GITHUB_CLIENT_SECRET"], url: "https://github.com/settings/applications/new", steps: ["Set Authorization callback URL to /api/auth/oauth/github/callback", "Copy Client ID + generate Client Secret"] },
    { section: "LinkedIn", vars: ["LINKEDIN_CLIENT_ID", "LINKEDIN_CLIENT_SECRET"], url: "https://www.linkedin.com/developers/apps/new", steps: ["Enable Sign In with LinkedIn using OpenID Connect", "Set redirect URL /api/auth/oauth/linkedin/callback"] },
    { section: "Stripe (Live)", vars: ["STRIPE_API_KEY"], url: "https://dashboard.stripe.com/apikeys", steps: ["Replace sk_test_emergent with sk_live_...", "Restart backend"] },
    { section: "Interactive Brokers", vars: ["IBKR_ACCOUNT_ID", "IBKR_GATEWAY_URL"], url: "https://www.interactivebrokers.com/en/trading/ib-api.php", steps: ["Run IB Client Portal Gateway", "Set IBKR_GATEWAY_URL to https://localhost:5000/v1/api"] },
    { section: "Zerodha Kite Connect", vars: ["ZERODHA_API_KEY", "ZERODHA_API_SECRET"], url: "https://kite.trade/", steps: ["Create a Kite Connect app", "Set redirect URI to your app"] },
    { section: "RAG Embeddings (upgrade)", vars: ["EMBEDDING_BACKEND=st", "ST_MODEL=all-MiniLM-L6-v2"], url: "https://www.sbert.net/", steps: ["pip install sentence-transformers", "Set EMBEDDING_BACKEND=st in backend/.env", "Restart backend — new documents get semantic embeddings"] },
  ];

  return (
    <div className="space-y-4" data-testid="setup-panel">
      <div className={`border p-4 ${mismatch ? "border-bear bg-[#2a0000]" : "border-gold bg-[color:var(--bg-surface)]"}`} data-testid="version-banner">
        <div className="text-[10px] font-mono-data tracking-widest mb-2" style={{ color: mismatch ? "#FF3B30" : "#FFD700" }}>
          ◆ DEPLOYMENT VERSION {mismatch ? "· ⚠ STALE DEPLOYMENT DETECTED" : ""}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
          <div><span className="text-[color:var(--text-muted)] font-mono-data">CLIENT COMMIT </span><span className="font-mono-data text-white">{clientCommit}</span></div>
          <div><span className="text-[color:var(--text-muted)] font-mono-data">SERVER COMMIT </span><span className={`font-mono-data ${mismatch ? "text-bear" : "text-white"}`}>{version?.commit || "…"}</span></div>
          <div><span className="text-[color:var(--text-muted)] font-mono-data">APP VERSION </span><span className="font-mono-data text-gold">{version?.version || process.env.REACT_APP_VERSION}</span></div>
          <div><span className="text-[color:var(--text-muted)] font-mono-data">BUILT </span><span className="font-mono-data text-white">{(version?.build_time || "").slice(0, 19).replace("T", " ")}</span></div>
        </div>
        {mismatch && (
          <div className="text-[11px] font-body text-bear mt-3">
            ⚠ Client bundle and server build don't match. Trigger a redeploy so both point to the same commit.
          </div>
        )}
      </div>

      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
        <div className="text-[10px] font-mono-data text-gold tracking-widest mb-2">◆ CURRENT PROVIDER STATUS</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {Object.entries(providers).map(([k, v]) => (
            <div key={k} className={`border ${v.configured ? "border-bull" : "border-[color:var(--border-default)]"} p-2`} data-testid={`prov-${k}`}>
              <div className="font-mono-data text-[10px] text-[color:var(--text-muted)] tracking-widest">{k.toUpperCase()}</div>
              <div className={`font-mono-data text-sm ${v.configured ? "text-bull" : "text-[color:var(--text-muted)]"}`}>{v.configured ? "✓ ACTIVE" : "◇ NOT SET"}</div>
            </div>
          ))}
          {brokerStatus && Object.entries(brokerStatus.providers || {}).map(([k, v]) => (
            <div key={k} className={`border ${v.configured ? "border-bull" : "border-[color:var(--border-default)]"} p-2`} data-testid={`prov-broker-${k}`}>
              <div className="font-mono-data text-[10px] text-[color:var(--text-muted)] tracking-widest">{k.toUpperCase()}</div>
              <div className={`font-mono-data text-sm ${v.configured ? "text-bull" : "text-[color:var(--text-muted)]"}`}>{v.configured ? "✓ ACTIVE" : "◇ PAPER"}</div>
            </div>
          ))}
          {ragBackend && (
            <div className="border border-gold p-2" data-testid="prov-rag">
              <div className="font-mono-data text-[10px] text-[color:var(--text-muted)] tracking-widest">RAG EMBED</div>
              <div className="font-mono-data text-sm text-gold">{ragBackend.backend}</div>
            </div>
          )}
        </div>
      </div>

      <div className="text-[11px] font-body text-[color:var(--text-secondary)]">
        Add these keys to <span className="font-mono-data text-gold">/app/backend/.env</span> and restart the backend. Grayed providers auto-activate the moment their env vars are populated.
      </div>

      {envGuide.map((g) => (
        <div key={g.section} className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4" data-testid={`guide-${g.section.replace(/ /g, "-")}`}>
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-sm text-white font-body">{g.section}</div>
            <a href={g.url} target="_blank" rel="noreferrer" className="text-[10px] font-mono-data text-gold underline tracking-widest">SIGNUP →</a>
          </div>
          <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">ENV KEYS</div>
          <div className="font-mono-data text-xs text-white bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] p-2 mb-2">
            {g.vars.map((v) => <div key={v}>{v}=</div>)}
          </div>
          <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">STEPS</div>
          <ol className="text-[11px] font-body text-[color:var(--text-secondary)] list-decimal ml-4 space-y-0.5">
            {g.steps.map((s) => <li key={s}>{s}</li>)}
          </ol>
        </div>
      ))}
    </div>
  );
}

function KPI({ label, value, tone }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : tone === "gold" ? "text-gold" : "text-white";
  return (
    <div className="p-4 border-r border-b border-[color:var(--border-default)]">
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 ${c}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
      <div className="text-[10px] font-mono-data text-white tracking-widest mb-3">{title}</div>
      {children}
    </div>
  );
}

function Row({ left, right }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-[color:var(--border-default)] last:border-0">
      <span className="text-[11px] font-mono-data text-[color:var(--text-secondary)] truncate">{left}</span>
      <span className="text-[11px] font-mono-data text-gold">{right}</span>
    </div>
  );
}

function AnalyticsPanel({ data, loading, onRefresh }) {
  if (loading && !data) {
    return (
      <div className="p-8 text-center text-xs font-mono-data text-[color:var(--text-muted)]" data-testid="analytics-loading">
        Loading production analytics…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-8 text-center" data-testid="analytics-empty">
        <button onClick={onRefresh} data-testid="analytics-load-btn" className="px-4 py-2 border border-[color:var(--border-default)] text-xs font-mono-data hover:bg-[color:var(--bg-hover)]">
          LOAD ANALYTICS
        </button>
      </div>
    );
  }
  const d = data;
  const ti = d.total_trade_ideas || {};
  const cred = d.credits_consumed || {};
  const usage = d.usage || {};
  const health = d.system_health || {};
  const users = d.active_users || {};
  const rev = d.revenue_usd || {};
  const nKey = `${d.window_days}d`;
  return (
    <div className="space-y-4" data-testid="analytics-panel">
      {/* Cost Alert Banner (non-intrusive, feature-flagged, only rendered when non-ok level) */}
      <CostAlertBanner alert={d.cost_alert} />

      {/* Meta */}
      <div className="flex items-center justify-between text-[10px] font-mono-data text-[color:var(--text-muted)]">
        <span data-testid="analytics-meta">
          WINDOW: {d.window_days}D · QUERY: {d.query_latency_ms}ms · GENERATED: {(d.generated_at || "").slice(0, 19).replace("T", " ")}Z
        </span>
        <button onClick={onRefresh} data-testid="analytics-refresh-btn" className="px-3 py-1 border border-[color:var(--border-default)] hover:bg-[color:var(--bg-hover)]">
          REFRESH
        </button>
      </div>

      {/* Row 1: primary counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]" data-testid="analytics-kpi-primary">
        <KPI label="TOTAL IDEAS" value={fmt(ti.lifetime || 0, 0)} tone="gold" />
        <KPI label={`IDEAS · ${nKey.toUpperCase()}`} value={fmt(ti[nKey] || 0, 0)} tone="bull" />
        <KPI label="IDEAS · 7D" value={fmt(ti["7d"] || 0, 0)} />
        <KPI label="IDEAS · 1D" value={fmt(ti["1d"] || 0, 0)} />
        <KPI label="AI SUCCESS" value={`${d.ai_success_rate_pct}%`} tone={d.ai_success_rate_pct >= 95 ? "bull" : "bear"} />
      </div>

      {/* Row 2: engine v2 guardrails */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border-default)]" data-testid="analytics-kpi-engine">
        <KPI label="CACHE HIT RATE" value={`${d.cache_hit_rate_pct}%`} tone="bull" />
        <KPI label="R:R CORRECTION" value={`${d.rr_correction_rate_pct}%`} tone="gold" />
        <KPI label="STOP CLAMPED" value={`${d.stop_clamp_rate_pct}%`} tone={d.stop_clamp_rate_pct > 10 ? "bear" : "bull"} />
        <KPI label="API ERROR RATE" value={`${d.api_error_rate_pct}%`} tone={d.api_error_rate_pct > 2 ? "bear" : "bull"} />
      </div>

      {/* Row 3: cost + latency */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border-default)]" data-testid="analytics-kpi-cost">
        <KPI label="AVG COST / IDEA" value={`$${(d.avg_ai_cost_per_idea_usd || 0).toFixed(6)}`} />
        <KPI label={`TOTAL AI $ · ${nKey.toUpperCase()}`} value={`$${fmt(d.total_ai_cost_nd_usd || 0, 2)}`} tone="bear" />
        <KPI label="AVG LATENCY" value={d.avg_latency_ms != null ? `${(d.avg_latency_ms/1000).toFixed(2)}s` : "—"} />
        <KPI label={`CREDITS BURNED · ${nKey.toUpperCase()}`} value={fmt(cred[nKey] || 0, 0)} />
      </div>

      {/* Row 4: users + revenue */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]" data-testid="analytics-kpi-users">
        <KPI label="DAU" value={fmt(users.dau || 0, 0)} tone="bull" />
        <KPI label="WAU" value={fmt(users.wau || 0, 0)} />
        <KPI label={`${nKey.toUpperCase()} ACTIVE`} value={fmt(users[`${d.window_days}au`] || 0, 0)} />
        <KPI label={`REVENUE · ${nKey.toUpperCase()}`} value={`$${fmt(rev[nKey] || 0, 2)}`} tone="gold" />
        <KPI label="LIFETIME REV" value={`$${fmt(rev.lifetime || 0, 2)}`} tone="gold" />
      </div>

      {/* Panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="MOST REQUESTED SYMBOLS">
          <div className="space-y-1" data-testid="analytics-top-symbols">
            {(d.top_symbols || []).map((s) => (
              <Row key={s.symbol} left={s.symbol} right={`${s.count} ideas`} />
            ))}
            {(d.top_symbols || []).length === 0 && <div className="text-[11px] text-[color:var(--text-muted)]">No data</div>}
          </div>
        </Panel>

        <Panel title="PROMPT VERSION DISTRIBUTION">
          <div className="space-y-1" data-testid="analytics-prompt-versions">
            {(d.prompt_version_distribution || []).map((p) => (
              <Row key={p.version} left={p.version} right={`${p.count} ideas`} />
            ))}
            {(d.prompt_version_distribution || []).length === 0 && <div className="text-[11px] text-[color:var(--text-muted)]">No data</div>}
          </div>
        </Panel>

        <Panel title="USAGE — DAILY / WEEKLY / MONTHLY">
          <div className="space-y-1" data-testid="analytics-usage">
            <Row left="Daily (1D)" right={`${fmt(usage.daily || 0, 0)} ideas`} />
            <Row left="Weekly (7D)" right={`${fmt(usage.weekly || 0, 0)} ideas`} />
            <Row left={`Monthly (${d.window_days}D)`} right={`${fmt(usage.monthly || 0, 0)} ideas`} />
          </div>
        </Panel>

        <Panel title="SYSTEM HEALTH">
          <div className="space-y-1" data-testid="analytics-system-health">
            <Row left="Overall status" right={(health.status || "unknown").toUpperCase()} />
            <Row left="Health-check latency" right={`${health.check_latency_ms ?? "?"} ms`} />
            {Object.entries(health.collections || {}).map(([k, v]) => (
              <Row key={k} left={`collection · ${k}`} right={v === "up" ? "UP" : String(v).toUpperCase()} />
            ))}
          </div>
        </Panel>
      </div>

      {/* Timeseries — 14-day sparklines, additive widget */}
      <TimeseriesWidget data={d._timeseries} />
    </div>
  );
}



function CostAlertBanner({ alert }) {
  if (!alert || !alert.enabled) return null;
  // Show when level is warning/critical OR projection blows past the critical line
  const showBanner = alert.level === "warning" || alert.level === "critical" || alert.projected_over_critical;
  if (!showBanner) return null;

  const isCritical = alert.level === "critical";
  const isProjectedOnly = alert.level === "ok" && alert.projected_over_critical;
  const borderCls = isCritical ? "border-bear" : "border-gold";
  const textCls = isCritical ? "text-bear" : "text-gold";
  const badge = isCritical ? "CRITICAL" : isProjectedOnly ? "PROJECTED OVER" : "WARNING";
  const message = alert.message || (isProjectedOnly
    ? `Straight-line projection ($${alert.estimated_month_end_usd}) exceeds critical threshold ($${alert.critical_threshold_usd}).`
    : "");

  return (
    <div
      data-testid="cost-alert-banner"
      data-alert-level={alert.level}
      className={`border ${borderCls} bg-[color:var(--bg-surface)] p-3 flex flex-wrap items-center gap-4 text-xs font-mono-data`}
    >
      <span data-testid="cost-alert-badge" className={`px-2 py-0.5 border ${borderCls} ${textCls} tracking-widest`}>
        ⚠ {badge}
      </span>
      <span className="text-white" data-testid="cost-alert-message">{message}</span>
      <span className="text-[color:var(--text-muted)]" data-testid="cost-alert-numbers">
        MTD ${alert.current_month_to_date_usd.toFixed(2)}
        &nbsp;·&nbsp; WARN ${alert.warning_threshold_usd.toFixed(2)}
        &nbsp;·&nbsp; CRIT ${alert.critical_threshold_usd.toFixed(2)}
        &nbsp;·&nbsp; {alert.percent_of_critical}% of CRIT
        &nbsp;·&nbsp; PROJ ${alert.estimated_month_end_usd.toFixed(2)} (day {alert.month_days_elapsed}/{alert.month_days_total})
      </span>
    </div>
  );
}

// ============================================================================
// TimeseriesWidget — additive 14-day sparklines widget.
// Uses pure inline SVG (no external chart library) to stay dependency-free.
// Renders 4 sparklines: Daily AI Cost, Daily Requests, Daily Cache Hits,
// Daily Credits Consumed. Backwards-compatible with the previous trade-ideas
// timeseries endpoint if new keys are absent.
// ============================================================================
function TimeseriesWidget({ data }) {
  if (!data) {
    return (
      <div className="border border-[color:var(--border-default)] p-4 text-xs font-mono-data text-[color:var(--text-muted)]"
           data-testid="timeseries-empty">
        Timeseries loading…
      </div>
    );
  }
  const days = data.days || 14;
  // Union of all dates present across every series so gaps stay aligned
  const dateSet = new Set();
  const bucket = { cost: {}, requests: {}, cache: {}, credits: {}, ideas: {} };
  (data.cost_series || []).forEach(r => { dateSet.add(r.date); bucket.cost[r.date] = r.usd; });
  (data.requests_series || []).forEach(r => { dateSet.add(r.date); bucket.requests[r.date] = r.count; });
  (data.cache_hits_series || []).forEach(r => { dateSet.add(r.date); bucket.cache[r.date] = r.count; });
  (data.credits_series || []).forEach(r => { dateSet.add(r.date); bucket.credits[r.date] = r.credits; });
  (data.series || []).forEach(r => { dateSet.add(r.date); bucket.ideas[r.date] = r.count; });

  // Build canonical N-day sorted timeline; back-fill missing dates
  const today = new Date();
  const dates = [];
  for (let i = days - 1; i >= 0; i--) {
    const dt = new Date(today);
    dt.setUTCDate(today.getUTCDate() - i);
    dates.push(dt.toISOString().slice(0, 10));
  }

  const series = [
    { key: "cost",     label: "Daily AI Cost",     tone: "bear",  fmt: (v) => `$${(v || 0).toFixed(4)}`, values: dates.map(d => bucket.cost[d] || 0) },
    { key: "requests", label: "Daily Requests",    tone: "gold",  fmt: (v) => `${v || 0}`,               values: dates.map(d => bucket.requests[d] || 0) },
    { key: "cache",    label: "Daily Cache Hits",  tone: "bull",  fmt: (v) => `${v || 0}`,               values: dates.map(d => bucket.cache[d] || 0) },
    { key: "credits",  label: "Credits Consumed",  tone: "gold",  fmt: (v) => `${v || 0}`,               values: dates.map(d => bucket.credits[d] || 0) },
  ];

  return (
    <div className="border border-[color:var(--border-default)]" data-testid="timeseries-widget">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--border-default)] text-[10px] font-mono-data text-[color:var(--text-muted)]">
        <span className="tracking-widest text-white">{days}-DAY TIMESERIES</span>
        <span data-testid="timeseries-meta">
          {dates[0]} → {dates[dates.length - 1]}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        {series.map(s => (
          <Sparkline
            key={s.key}
            testId={`timeseries-${s.key}`}
            label={s.label}
            tone={s.tone}
            dates={dates}
            values={s.values}
            formatValue={s.fmt}
          />
        ))}
      </div>
    </div>
  );
}

function Sparkline({ label, values, dates, tone = "gold", formatValue, testId }) {
  const W = 320, H = 60, PAD = 4;
  const max = Math.max(1, ...values);
  const n = values.length || 1;
  const stepX = (W - PAD * 2) / Math.max(n - 1, 1);
  const pts = values.map((v, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - ((v || 0) / max) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const colorVar = tone === "bear" ? "var(--bear)" : tone === "bull" ? "var(--bull)" : "var(--gold)";
  const areaPts = [`${PAD},${H - PAD}`, ...pts, `${W - PAD},${H - PAD}`].join(" ");
  const total = values.reduce((a, b) => a + (b || 0), 0);
  const last = values[values.length - 1] || 0;

  return (
    <div className="border-b md:border-r border-[color:var(--border-default)] p-3 last:border-b-0 md:last:border-r-0" data-testid={testId}>
      <div className="flex items-center justify-between text-[10px] font-mono-data text-[color:var(--text-muted)] mb-1">
        <span className="tracking-widest text-white">{label}</span>
        <span data-testid={`${testId}-total`}>Σ {formatValue ? formatValue(total) : total}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none" role="img" aria-label={label}>
        <polygon points={areaPts} style={{ fill: colorVar, fillOpacity: 0.12 }} />
        <polyline
          points={pts.join(" ")}
          style={{ stroke: colorVar, fill: "none" }}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
        {values.map((v, i) => {
          const x = PAD + i * stepX;
          const y = H - PAD - ((v || 0) / max) * (H - PAD * 2);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="1.6" style={{ fill: colorVar }} />
              <title>{`${dates[i]} — ${formatValue ? formatValue(v) : v}`}</title>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">
        <span>{dates[0]?.slice(5)}</span>
        <span data-testid={`${testId}-latest`}>latest: {formatValue ? formatValue(last) : last}</span>
        <span>{dates[dates.length - 1]?.slice(5)}</span>
      </div>
    </div>
  );
}



// ============================================================================
// LearningPanel — AI Learning Framework read-only dashboard.
// 6 panels: strategy accuracy, symbol accuracy, prompt performance,
// confidence calibration, user behaviour, model drift.
// Zero writes except the admin-triggered manual resolver tick.
// ============================================================================
function LearningPanel({ data, loading, days, sectorFilter, onChangeSector, onChangeDays,
                         onRefresh, onResolveNow, onBackfillPrompt,
                         profile, profileLoading, profileUserId, onChangeProfileUserId, onLoadProfile }) {
  if (loading && !data) {
    return (
      <div className="p-8 text-center text-xs font-mono-data text-[color:var(--text-muted)]" data-testid="learning-loading">
        Loading AI learning framework…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-8 text-center" data-testid="learning-empty">
        <button onClick={onRefresh} data-testid="learning-load-btn" className="px-4 py-2 border border-[color:var(--border-default)] text-xs font-mono-data hover:bg-[color:var(--bg-hover)]">
          LOAD LEARNING FRAMEWORK
        </button>
      </div>
    );
  }
  const h = data.health || {};
  const sectorOpts = data.sectorsList || [];
  return (
    <div className="space-y-4" data-testid="learning-panel">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 border border-[color:var(--border-default)] p-3">
        <div className="text-[10px] font-mono-data text-gold tracking-widest">
          ◆ AI LEARNING FRAMEWORK · READ-ONLY · SIGNAL FILTER OFF
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono-data">
          <span className="text-[color:var(--text-muted)]">WINDOW</span>
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              data-testid={`learning-window-${d}`}
              onClick={() => onChangeDays(d)}
              className={`px-2 py-0.5 border ${days === d ? "border-white text-white bg-[color:var(--bg-hover)]" : "border-[color:var(--border-default)] text-[color:var(--text-secondary)]"}`}
            >
              {d}D
            </button>
          ))}
          <span className="text-[color:var(--text-muted)] ml-2">SECTOR</span>
          <select
            data-testid="learning-sector-filter"
            value={sectorFilter}
            onChange={(e) => onChangeSector(e.target.value)}
            className="bg-transparent border border-[color:var(--border-default)] text-white px-2 py-0.5 text-[11px] font-mono-data"
          >
            <option value="">ALL</option>
            {sectorOpts.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            data-testid="learning-refresh-btn"
            onClick={onRefresh}
            className="ml-2 px-2 py-0.5 border border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"
          >
            REFRESH
          </button>
          <button
            data-testid="learning-resolve-now-btn"
            onClick={onResolveNow}
            className="px-2 py-0.5 border border-gold text-gold hover:bg-[color:var(--bg-hover)]"
          >
            RESOLVE NOW
          </button>
          <button
            data-testid="learning-backfill-prompt-btn"
            onClick={onBackfillPrompt}
            className="px-2 py-0.5 border border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"
          >
            BACKFILL PROMPT
          </button>
        </div>
      </div>

      {/* Health strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border border-[color:var(--border-default)]" data-testid="learning-health-strip">
        <KPI label="OUTCOMES" value={h.outcomes_total ?? 0} />
        <KPI label="RESOLVED" value={h.outcomes_resolved ?? 0} tone="bull" />
        <KPI label="OPEN" value={h.outcomes_open ?? 0} />
        <KPI label="WIN RATE" value={h.win_rate_pct == null ? "—" : `${h.win_rate_pct}%`} tone="gold" />
        <KPI label="MANUAL OVERRIDES" value={h.manual_overrides ?? 0} />
        <KPI label="SECTORS CACHED" value={h.sectors_cached ?? 0} />
      </div>

      {/* Signal Filter Readiness — top-priority diagnostic. Signal Filter stays OFF. */}
      <ReadinessMonitor data={data.readiness} />

      {/* Regime Intelligence — historical performance by market regime. */}
      <RegimeIntelligencePanel data={data.regime} />

      {/* Trader Intelligence Inspector — admin can look up any user's profile. */}
      <TraderProfileInspector
        userId={profileUserId}
        onChangeUserId={onChangeProfileUserId}
        onLoad={onLoadProfile}
        profile={profile}
        loading={profileLoading}
        leaderboard={data.leaderboard}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title={`STRATEGY ACCURACY${sectorFilter ? ` · ${sectorFilter.toUpperCase()}` : ""}`}>
          <AccuracyTable rows={data.strategy?.rows || []} keyLabel="STRATEGY" testId="learning-strategy-table" />
        </Panel>

        <Panel title={`SYMBOL ACCURACY${sectorFilter ? ` · ${sectorFilter.toUpperCase()}` : ""}`}>
          <AccuracyTable rows={data.symbol?.rows || []} keyLabel="SYMBOL" testId="learning-symbol-table" />
        </Panel>

        <Panel title="SECTOR ACCURACY">
          <AccuracyTable rows={data.sector?.rows || []} keyLabel="SECTOR" testId="learning-sector-table" />
        </Panel>

        <Panel title="PROMPT PERFORMANCE">
          <AccuracyTable rows={data.prompt?.rows || []} keyLabel="PROMPT VER" testId="learning-prompt-table" />
        </Panel>

        <Panel title="CONFIDENCE CALIBRATION">
          <CalibrationTable data={data.calibration} />
        </Panel>

        <Panel title="USER BEHAVIOUR">
          <UserBehaviourTable data={data.userBehavior} />
        </Panel>

        <Panel title="MODEL DRIFT · WEEKLY">
          <DriftTable data={data.drift} />
        </Panel>
      </div>
    </div>
  );
}

function AccuracyTable({ rows, keyLabel, testId }) {
  if (!rows || rows.length === 0) {
    return <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]" data-testid={`${testId}-empty`}>No resolved samples in window.</div>;
  }
  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-[1.6fr,50px,50px,50px,60px] gap-2 px-1 py-1 text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border-default)]">
        <span>{keyLabel}</span><span>N</span><span>W</span><span>L</span><span>WIN%</span>
      </div>
      {rows.slice(0, 12).map((r) => (
        <div key={String(r.key)} className="grid grid-cols-[1.6fr,50px,50px,50px,60px] gap-2 px-1 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0" data-testid={`${testId}-row-${r.key}`}>
          <span className="text-white truncate" title={String(r.key)}>{r.key}</span>
          <span className="text-[color:var(--text-secondary)]">{r.resolved}</span>
          <span className="text-bull">{r.wins}</span>
          <span className="text-bear">{r.losses}</span>
          <span className={r.win_rate_pct >= 55 ? "text-bull" : r.win_rate_pct <= 45 ? "text-bear" : "text-gold"}>
            {r.win_rate_pct == null ? "—" : `${r.win_rate_pct}%`}
          </span>
        </div>
      ))}
    </div>
  );
}

function CalibrationTable({ data }) {
  if (!data || !data.buckets || data.buckets.length === 0) {
    return <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]" data-testid="learning-calibration-empty">No resolved samples in window.</div>;
  }
  return (
    <div data-testid="learning-calibration-table">
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mb-2">
        BRIER SCORE <span className="text-gold">{data.brier_score ?? "—"}</span> · N <span className="text-white">{data.n_resolved}</span>
        <span className="text-[color:var(--text-muted)]"> · lower brier = better calibrated</span>
      </div>
      <div className="grid grid-cols-[1fr,60px,60px,60px,80px] gap-2 px-1 py-1 text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border-default)]">
        <span>BUCKET</span><span>N</span><span>PRED%</span><span>ACT%</span><span>GAP</span>
      </div>
      {data.buckets.map((b) => (
        <div key={b.bucket} className="grid grid-cols-[1fr,60px,60px,60px,80px] gap-2 px-1 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0" data-testid={`learning-calibration-row-${b.bucket_start}`}>
          <span className="text-white">{b.bucket}</span>
          <span className="text-[color:var(--text-secondary)]">{b.n}</span>
          <span className="text-gold">{b.avg_predicted_pct ?? "—"}</span>
          <span className="text-white">{b.actual_win_rate_pct ?? "—"}</span>
          <span className={Math.abs(b.calibration_gap_pct || 0) < 5 ? "text-bull" : Math.abs(b.calibration_gap_pct || 0) < 15 ? "text-gold" : "text-bear"}>
            {b.calibration_gap_pct == null ? "—" : `${b.calibration_gap_pct > 0 ? "+" : ""}${b.calibration_gap_pct}`}
          </span>
        </div>
      ))}
    </div>
  );
}

function UserBehaviourTable({ data }) {
  if (!data) return null;
  const counts = data.counts || {};
  const rates = data.rates_pct || {};
  const perf = data.performance_by_action || {};
  const actions = ["accepted", "saved", "ignored", "no_action"];
  return (
    <div data-testid="learning-userbehavior-table">
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mb-2">
        TOTAL IDEAS <span className="text-white">{data.total_ideas}</span> · WINDOW <span className="text-white">{data.window_days}D</span>
      </div>
      <div className="grid grid-cols-[1fr,60px,60px,80px] gap-2 px-1 py-1 text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border-default)]">
        <span>ACTION</span><span>N</span><span>RATE%</span><span>WIN%</span>
      </div>
      {actions.map((a) => (
        <div key={a} className="grid grid-cols-[1fr,60px,60px,80px] gap-2 px-1 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0" data-testid={`learning-userbehavior-row-${a}`}>
          <span className="text-white">{a}</span>
          <span className="text-[color:var(--text-secondary)]">{counts[a] || 0}</span>
          <span className="text-gold">{rates[a] || 0}%</span>
          <span className={perf[a]?.win_rate_pct >= 55 ? "text-bull" : perf[a]?.win_rate_pct <= 45 ? "text-bear" : "text-white"}>
            {perf[a]?.win_rate_pct == null ? "—" : `${perf[a].win_rate_pct}%`}
          </span>
        </div>
      ))}
    </div>
  );
}

function DriftTable({ data }) {
  if (!data || !data.buckets || data.buckets.length === 0) {
    return <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]" data-testid="learning-drift-empty">No resolved samples in window.</div>;
  }
  const values = data.buckets.map((b) => b.win_rate_pct ?? 0);
  const dates = data.buckets.map((b) => b.bucket_start);
  return (
    <div data-testid="learning-drift-table">
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mb-2">
        {data.buckets.length} × {data.bucket_days || 7}-day bucket{data.buckets.length === 1 ? "" : "s"} · window {data.window_days}D
      </div>
      <Sparkline
        testId="learning-drift-sparkline"
        label="ROLLING WIN-RATE %"
        tone="gold"
        dates={dates}
        values={values}
        formatValue={(v) => `${v ?? 0}%`}
      />
      <div className="grid grid-cols-[1fr,60px,60px,60px,80px] gap-2 px-1 py-1 text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border-default)] mt-2">
        <span>WEEK OF</span><span>N</span><span>W</span><span>L</span><span>WIN%</span>
      </div>
      {data.buckets.map((b) => (
        <div key={b.bucket_start} className="grid grid-cols-[1fr,60px,60px,60px,80px] gap-2 px-1 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0" data-testid={`learning-drift-row-${b.bucket_start}`}>
          <span className="text-white">{b.bucket_start}</span>
          <span className="text-[color:var(--text-secondary)]">{b.n}</span>
          <span className="text-bull">{b.wins}</span>
          <span className="text-bear">{b.losses}</span>
          <span className={b.win_rate_pct >= 55 ? "text-bull" : b.win_rate_pct <= 45 ? "text-bear" : "text-gold"}>
            {b.win_rate_pct == null ? "—" : `${b.win_rate_pct}%`}
          </span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// ReadinessMonitor — Signal Filter Manual-Enable Diagnostic.
// STRICTLY read-only. Never enables Signal Filter. Displays 4 gates:
//   1. Resolved sample count
//   2. Calibration quality (Brier score)
//   3. Win-rate stability (weekly std-dev)
//   4. Prompt-version consistency (dominant version share)
// Aggregate recommendation: not_ready | review | ready_for_manual_enable.
// ============================================================================
function ReadinessMonitor({ data }) {
  if (!data) return null;
  const rec = data.recommendation || "not_ready";
  const tone = rec === "ready_for_manual_enable" ? "bull" : rec === "review" ? "gold" : "bear";
  const border = tone === "bull" ? "border-bull" : tone === "bear" ? "border-bear" : "border-gold";
  const text = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : "text-gold";
  const badge = rec === "ready_for_manual_enable"
    ? "READY FOR MANUAL ENABLE"
    : rec === "review" ? "REVIEW" : "NOT READY";
  const m = data.metrics || {};
  const scores = data.scores || {};
  const thr = data.thresholds || {};

  const gate = (label, key, valueDisplay, tip) => {
    const s = scores[key];
    const cls = s === "pass" ? "text-bull border-bull"
              : s === "review" ? "text-gold border-gold"
              : "text-bear border-bear";
    const emoji = s === "pass" ? "✓" : s === "review" ? "◐" : "✗";
    return (
      <div className={`border ${cls} p-3`} data-testid={`readiness-gate-${key}`}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
        <div className="flex items-baseline justify-between mt-1">
          <div className={`font-mono-data text-lg ${cls.split(" ")[0]}`}>{valueDisplay}</div>
          <div className={`text-xs font-mono-data ${cls.split(" ")[0]}`}>{emoji} {(s || "").toUpperCase()}</div>
        </div>
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">{tip}</div>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="readiness-monitor">
      <div className={`border ${border} p-3`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
            ◆ SIGNAL FILTER READINESS MONITOR · READ-ONLY DIAGNOSTIC
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 border ${border} ${text} text-[11px] font-mono-data tracking-widest`} data-testid="readiness-recommendation">
              {badge}
            </span>
            <span
              className={`px-2 py-0.5 border ${data.signal_filter_currently_enabled ? "border-bear text-bear" : "border-[color:var(--border-default)] text-[color:var(--text-muted)]"} text-[10px] font-mono-data tracking-widest`}
              data-testid="readiness-filter-state"
            >
              FILTER {data.signal_filter_currently_enabled ? "ON" : "OFF"}
            </span>
          </div>
        </div>
        <div className={`text-xs font-body mt-2 ${text}`} data-testid="readiness-banner">{data.banner}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {gate(
          "SAMPLES",
          "samples",
          `${m.resolved_samples ?? 0}`,
          `ready ≥ ${thr.ready?.min_samples} · review ≥ ${thr.review?.min_samples}`
        )}
        {gate(
          "CALIBRATION (BRIER)",
          "calibration",
          m.brier_score == null ? "—" : String(m.brier_score),
          `ready ≤ ${thr.ready?.max_brier} · review ≤ ${thr.review?.max_brier}`
        )}
        {gate(
          "STABILITY (STDDEV)",
          "stability",
          m.weekly_win_rate_stddev == null ? "—" : `${m.weekly_win_rate_stddev}%`,
          `ready ≤ ${thr.ready?.max_stability_std} · review ≤ ${thr.review?.max_stability_std} · buckets ${m.weekly_bucket_count ?? 0}`
        )}
        {gate(
          "PROMPT CONSISTENCY",
          "prompt_consistency",
          m.prompt_version_dominant_share == null ? "—" : `${(m.prompt_version_dominant_share * 100).toFixed(0)}%`,
          `${m.prompt_version_dominant || "—"} · ready ≥ ${(thr.ready?.min_prompt_share || 0) * 100}% · review ≥ ${(thr.review?.min_prompt_share || 0) * 100}%`
        )}
      </div>

      {(data.reasons || []).length > 0 && (
        <div className="border border-[color:var(--border-default)] p-3" data-testid="readiness-reasons">
          <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-1">GAPS BLOCKING READY</div>
          <ul className="text-[11px] font-mono-data text-[color:var(--text-secondary)] list-disc ml-4 space-y-0.5">
            {data.reasons.map((r) => <li key={r}>{r}</li>)}
          </ul>
        </div>
      )}

      {/* Countdown to next tier — reads-only helper for planning. */}
      {data.countdown && (
        <div className="border border-[color:var(--border-default)] p-3" data-testid="readiness-countdown">
          <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-2">
            COUNTDOWN TO {data.countdown.next_tier || "NEXT TIER"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono-data">
            <CountdownCell
              label="SAMPLES"
              needReview={data.countdown.samples_to_review}
              needReady={data.countdown.samples_to_ready}
              unit=""
            />
            <CountdownCell
              label="BRIER Δ"
              needReview={data.countdown.brier_delta_to_review}
              needReady={data.countdown.brier_delta_to_ready}
              unit=""
              formatter={(v) => (v == null ? "—" : v.toFixed(4))}
            />
            <CountdownCell
              label="STABILITY Δ"
              needReview={data.countdown.stability_delta_to_review}
              needReady={data.countdown.stability_delta_to_ready}
              unit="%"
              extraNote={
                (data.countdown.weekly_buckets_needed_for_stability || 0) > 0
                  ? `${data.countdown.weekly_buckets_needed_for_stability} more weekly bucket${data.countdown.weekly_buckets_needed_for_stability === 1 ? "" : "s"} needed`
                  : null
              }
            />
            <CountdownCell
              label="PROMPT SHARE Δ"
              needReview={data.countdown.prompt_share_delta_to_review}
              needReady={data.countdown.prompt_share_delta_to_ready}
              unit=""
              formatter={(v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function CountdownCell({ label, needReview, needReady, unit, formatter, extraNote }) {
  const fmt = formatter || ((v) => (v == null ? "—" : `${v}${unit || ""}`));
  const atReview = needReview === 0 || needReview === 0.0;
  const atReady = needReady === 0 || needReady === 0.0;
  const tone = atReady ? "text-bull border-bull" : atReview ? "text-gold border-gold" : "text-bear border-bear";
  return (
    <div className={`border ${tone} p-2`}>
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className="flex items-center justify-between mt-1">
        <div>
          <span className="text-[10px] text-[color:var(--text-muted)]">→REV</span>
          <span className="ml-1">{fmt(needReview)}</span>
        </div>
        <div>
          <span className="text-[10px] text-[color:var(--text-muted)]">→RDY</span>
          <span className="ml-1">{fmt(needReady)}</span>
        </div>
      </div>
      {extraNote && <div className="text-[10px] text-[color:var(--text-muted)] mt-1">{extraNote}</div>}
    </div>
  );
}

// ============================================================================
// TraderProfileInspector — admin can pull any user's Trader Intelligence Profile
// plus view a DNA-ranked leaderboard of the most engaged users.
// ============================================================================
function TraderProfileInspector({ userId, onChangeUserId, onLoad, profile, loading, leaderboard }) {
  const rows = leaderboard?.rows || [];
  return (
    <div className="border border-[color:var(--border-default)] p-3 space-y-3" data-testid="trader-intelligence-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] font-mono-data text-gold tracking-widest">
          ◆ TRADER INTELLIGENCE PROFILE · READ-ONLY
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono-data">
          <input
            data-testid="profile-userid-input"
            type="text"
            value={userId}
            onChange={(e) => onChangeUserId(e.target.value)}
            placeholder="user_id"
            className="bg-transparent border border-[color:var(--border-default)] text-white px-2 py-0.5 w-64"
          />
          <button
            data-testid="profile-load-btn"
            onClick={() => onLoad(userId)}
            disabled={!userId || loading}
            className={`px-2 py-0.5 border border-gold text-gold ${(!userId || loading) ? "opacity-40 cursor-not-allowed" : "hover:bg-[color:var(--bg-hover)]"}`}
          >
            {loading ? "…" : "LOAD PROFILE"}
          </button>
        </div>
      </div>

      {profile ? <ProfileDetailBlock profile={profile} /> : (
        <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]" data-testid="profile-empty">
          Enter a user_id (or click a row below) to load a trader intelligence profile.
        </div>
      )}

      {/* DNA Leaderboard */}
      <div>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-1">
          DNA LEADERBOARD · TOP {leaderboard?.count ?? 0} · {leaderboard?.window_days ?? 180}D
        </div>
        {rows.length === 0 ? (
          <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]" data-testid="profile-leaderboard-empty">
            No engaged users in window.
          </div>
        ) : (
          <div data-testid="profile-leaderboard">
            <div className="grid grid-cols-[1fr,90px,80px,80px,90px,120px] gap-2 px-1 py-1 text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] border-b border-[color:var(--border-default)]">
              <span>USER</span><span>DNA</span><span>SEEN</span><span>ACCEPT</span><span>WIN%</span><span>STYLE</span>
            </div>
            {rows.map((r) => (
              <button
                key={r.user_id}
                data-testid={`profile-leaderboard-row-${r.user_id}`}
                onClick={() => { onChangeUserId(r.user_id); onLoad(r.user_id); }}
                className="w-full grid grid-cols-[1fr,90px,80px,80px,90px,120px] gap-2 px-1 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0 hover:bg-[color:var(--bg-hover)] text-left"
              >
                <span className="text-white truncate">{r.name || r.email || r.user_id.slice(0, 24)}</span>
                <span className={r.dna_band === "platinum" ? "text-white" : r.dna_band === "gold" ? "text-gold" : r.dna_band === "silver" ? "text-[color:var(--text-secondary)]" : "text-bear"}>
                  {r.dna_score} · {r.dna_band}
                </span>
                <span className="text-[color:var(--text-secondary)]">{r.ideas_seen}</span>
                <span className="text-bull">{r.accepted}</span>
                <span className={r.win_rate_pct >= 55 ? "text-bull" : r.win_rate_pct <= 45 ? "text-bear" : "text-gold"}>
                  {r.win_rate_pct == null ? "—" : `${r.win_rate_pct}%`}
                </span>
                <span className="text-white truncate">{r.trading_style || "—"}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileDetailBlock({ profile }) {
  const p = profile;
  const perf = p.accepted_performance || {};
  const dna = p.trading_dna || {};
  const lp = p.learning_progress || {};
  const dnaTone = dna.band === "platinum" ? "text-white border-white"
                 : dna.band === "gold" ? "text-gold border-gold"
                 : dna.band === "silver" ? "text-[color:var(--text-secondary)] border-[color:var(--text-secondary)]"
                 : "text-bear border-bear";
  const lpTone = lp.tier === "elite" ? "text-gold border-gold"
                : lp.tier === "seasoned" ? "text-bull border-bull"
                : lp.tier === "developing" ? "text-[color:var(--text-secondary)] border-[color:var(--text-secondary)]"
                : "text-bear border-bear";
  return (
    <div className="space-y-3" data-testid="profile-detail">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* DNA */}
        <div className={`border p-3 ${dnaTone}`} data-testid="profile-dna-card">
          <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">TRADING DNA</div>
          <div className="flex items-baseline justify-between mt-1">
            <div className="font-mono-data text-3xl">{dna.score ?? "—"}</div>
            <div className="text-xs font-mono-data uppercase">{dna.band || "—"}</div>
          </div>
          <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-2">
            win-rate {dna.components?.win_rate_weight ?? 0} · engagement {dna.components?.engagement_weight ?? 0} · R:R {dna.components?.risk_reward_weight ?? 0} · consistency {dna.components?.consistency_weight ?? 0}
          </div>
        </div>
        {/* Learning progress */}
        <div className={`border p-3 ${lpTone}`} data-testid="profile-learning-card">
          <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">LEARNING PROGRESS</div>
          <div className="flex items-baseline justify-between mt-1">
            <div className="font-mono-data text-3xl">{lp.score ?? "—"}</div>
            <div className="text-xs font-mono-data uppercase">{lp.tier || "—"}</div>
          </div>
          <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-2">
            engagement {lp.components?.engagement} · feedback {lp.components?.feedback_density} · variety {lp.components?.variety}
          </div>
        </div>
      </div>

      {/* Numbers grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]" data-testid="profile-kpis">
        <KPI label="IDEAS SEEN" value={p.ideas_seen ?? 0} />
        <KPI label="ACCEPTED" value={p.actions?.accepted ?? 0} tone="bull" />
        <KPI label="SAVED" value={p.actions?.saved ?? 0} tone="gold" />
        <KPI label="IGNORED" value={p.actions?.ignored ?? 0} tone="bear" />
        <KPI label="ENGAGEMENT" value={p.actions?.engagement_rate == null ? "—" : `${Math.round(p.actions.engagement_rate * 100)}%`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]" data-testid="profile-accepted-perf">
        <KPI label="ACC WIN RATE" value={perf.win_rate_pct == null ? "—" : `${perf.win_rate_pct}%`} tone="gold" />
        <KPI label="AVG HOLDING" value={perf.avg_holding_days == null ? "—" : `${perf.avg_holding_days}d`} />
        <KPI label="AVG R:R" value={perf.avg_risk_reward == null ? "—" : `${perf.avg_risk_reward}`} />
        <KPI label="CONF PREF" value={perf.avg_confidence_pct == null ? "—" : `${perf.avg_confidence_pct}%`} />
        <KPI label="STYLE" value={p.trading_style || "—"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-[color:var(--border-default)] p-3" data-testid="profile-sectors">
          <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-2">PREFERRED SECTORS</div>
          {p.preferred_sectors?.length === 0 && <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]">No sector preference yet.</div>}
          {(p.preferred_sectors || []).map((s) => (
            <div key={s.key} className="grid grid-cols-[1fr,60px,80px] gap-2 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0">
              <span className="text-white">{s.key}</span>
              <span className="text-[color:var(--text-secondary)]">{s.n}</span>
              <span className={s.win_rate_pct >= 55 ? "text-bull" : s.win_rate_pct <= 45 ? "text-bear" : "text-gold"}>
                {s.win_rate_pct == null ? "—" : `${s.win_rate_pct}%`}
              </span>
            </div>
          ))}
        </div>
        <div className="border border-[color:var(--border-default)] p-3" data-testid="profile-strategies">
          <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-2">PREFERRED STRATEGIES</div>
          {p.preferred_strategies?.length === 0 && <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]">No strategy preference yet.</div>}
          {(p.preferred_strategies || []).map((s) => (
            <div key={s.key} className="grid grid-cols-[1fr,60px,80px] gap-2 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0">
              <span className="text-white truncate" title={s.key}>{s.key}</span>
              <span className="text-[color:var(--text-secondary)]">{s.n}</span>
              <span className={s.win_rate_pct >= 55 ? "text-bull" : s.win_rate_pct <= 45 ? "text-bear" : "text-gold"}>
                {s.win_rate_pct == null ? "—" : `${s.win_rate_pct}%`}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



// ============================================================================
// RegimeIntelligencePanel — win rate, avg R:R, best strategies, and confidence
// calibration by market regime.  Never fabricates: any bucket below the
// sample floor renders "Collecting historical evidence."
// ============================================================================
function RegimeIntelligencePanel({ data }) {
  if (!data) return null;
  const cr = data.current_regime || {};
  const perf = data.performance_by_regime || {};
  const best = data.best_strategies_by_regime || {};
  const calib = data.calibration_by_regime || {};
  const regimeKeys = ["bull", "sideways", "bear"];
  const regimeTone = (r) => r === "bull" ? "border-bull text-bull"
                          : r === "bear" ? "border-bear text-bear"
                          : r === "sideways" ? "border-gold text-gold"
                          : "border-[color:var(--border-default)] text-[color:var(--text-muted)]";
  return (
    <div className="border border-[color:var(--border-default)] p-3 space-y-4" data-testid="regime-intelligence-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] font-mono-data text-gold tracking-widest">
          ◆ REGIME INTELLIGENCE · HISTORICAL PERFORMANCE BY MARKET REGIME
        </div>
        <div className={`px-2 py-0.5 border ${regimeTone(cr.display)} text-[11px] font-mono-data tracking-widest`} data-testid="regime-intel-current-badge">
          CURRENT · {(cr.display || "unknown").toUpperCase()}
          {cr.deviation_pct != null && (
            <span className="ml-2 text-[color:var(--text-muted)] normal-case">
              {cr.proxy_symbol} {cr.close} vs 50d {cr.sma_50} ({cr.deviation_pct > 0 ? "+" : ""}{cr.deviation_pct}%)
            </span>
          )}
        </div>
      </div>

      {/* Row 1 — performance strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {regimeKeys.map((k) => (
          <RegimePerfCard
            key={k}
            regime={k}
            tone={regimeTone(k)}
            data={perf[k]}
          />
        ))}
      </div>

      {/* Row 2 — best strategies per regime */}
      <div>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-2">
          BEST STRATEGIES BY REGIME · min {data.min_samples_strategy} resolved
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {regimeKeys.map((k) => (
            <BestStrategiesCard key={k} regime={k} tone={regimeTone(k)} rows={best[k] || []} />
          ))}
        </div>
      </div>

      {/* Row 3 — confidence calibration per regime */}
      <div>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-2">
          CONFIDENCE CALIBRATION BY REGIME · min {data.min_samples} resolved per bucket
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {regimeKeys.map((k) => (
            <RegimeCalibrationCard key={k} regime={k} tone={regimeTone(k)} rows={calib[k] || []} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RegimePerfCard({ regime, tone, data }) {
  if (!data || data.n === 0) {
    return (
      <div className={`border ${tone} p-3`} data-testid={`regime-perf-${regime}-empty`}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{regime.toUpperCase()}</div>
        <div className="text-[11px] font-mono-data text-gold mt-2">Collecting historical evidence.</div>
      </div>
    );
  }
  const wr = data.win_rate_pct;
  return (
    <div className={`border ${tone} p-3`} data-testid={`regime-perf-${regime}`}>
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{regime.toUpperCase()}</div>
      <div className="flex items-baseline justify-between mt-1">
        <div className={`font-mono-data text-3xl ${tone.split(" ")[1]}`}>{wr == null ? "—" : `${wr}%`}</div>
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]">n {data.n}</div>
      </div>
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">
        {data.wins}W · {data.losses}L · avg R:R {data.avg_risk_reward ?? "—"} · hold {data.avg_days_to_outcome ?? "—"}d
      </div>
    </div>
  );
}

function BestStrategiesCard({ regime, tone, rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className={`border ${tone} p-3`} data-testid={`regime-strategies-${regime}-empty`}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{regime.toUpperCase()}</div>
        <div className="text-[11px] font-mono-data text-gold mt-2">Collecting historical evidence.</div>
      </div>
    );
  }
  return (
    <div className={`border ${tone} p-3`} data-testid={`regime-strategies-${regime}`}>
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-2">{regime.toUpperCase()}</div>
      {rows.map((r) => (
        <div key={r.strategy} className="grid grid-cols-[1fr,50px,50px,60px] gap-2 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0" data-testid={`regime-strategies-${regime}-row-${r.strategy}`}>
          <span className="text-white truncate" title={r.strategy}>{r.strategy}</span>
          <span className="text-[color:var(--text-secondary)]">{r.n}</span>
          <span className="text-[color:var(--text-secondary)]">{r.avg_risk_reward ?? "—"}</span>
          <span className={r.win_rate_pct >= 55 ? "text-bull" : r.win_rate_pct <= 45 ? "text-bear" : "text-gold"}>
            {r.win_rate_pct == null ? "—" : `${r.win_rate_pct}%`}
          </span>
        </div>
      ))}
    </div>
  );
}

function RegimeCalibrationCard({ regime, tone, rows }) {
  return (
    <div className={`border ${tone} p-3`} data-testid={`regime-calibration-${regime}`}>
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-2">{regime.toUpperCase()}</div>
      {(rows || []).map((b) => {
        const collecting = b.status === "collecting_evidence";
        return (
          <div key={b.band} className="grid grid-cols-[70px,60px,60px,60px,80px] gap-2 py-1 text-[11px] font-mono-data border-b border-[color:var(--border-default)] last:border-0" data-testid={`regime-calibration-${regime}-${b.band}`}>
            <span className="text-white">{b.band}</span>
            <span className="text-[color:var(--text-secondary)]">{b.n}</span>
            {collecting ? (
              <span className="col-span-3 text-gold">Collecting historical evidence.</span>
            ) : (
              <>
                <span className="text-gold">{b.avg_predicted_pct ?? "—"}</span>
                <span className="text-white">{b.actual_win_rate_pct ?? "—"}</span>
                <span className={Math.abs(b.calibration_gap_pct || 0) < 5 ? "text-bull" : Math.abs(b.calibration_gap_pct || 0) < 15 ? "text-gold" : "text-bear"}>
                  {b.calibration_gap_pct == null ? "—" : `${b.calibration_gap_pct > 0 ? "+" : ""}${b.calibration_gap_pct}`}
                </span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

